import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { appConfig } from '@/config/app';
import {
  ASSIGNABLE_ROLES,
  USER_ROLE_LABELS,
} from '@/config/permissions';
import { listUsersByCompany, saveUserProfile } from '@/services/auth';
import type { UserProfile, UserProfileInput, UserRole } from '@/types/user';
import { modulePaths } from '@/routes/paths';
import { EntityAuditTrail } from '@/shared/components/EntityAuditTrail';
import { useAuth } from '@/shared/hooks/useAuth';

type FilterTab = 'all' | 'pending' | 'active';

const emptyForm: UserProfileInput = {
  uid: '',
  email: '',
  displayName: '',
  photoURL: null,
  companyId: appConfig.companyId,
  role: 'commercial',
  active: true,
  provider: 'email',
};

export function UsersPage() {
  const { profile: currentProfile } = useAuth();
  const isAdminOnly = currentProfile?.role === 'admin';
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<UserProfileInput>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<FilterTab>('pending');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listUsersByCompany();
      setUsers(
        list.sort((a, b) => {
          if (a.role === 'pending' && b.role !== 'pending') return -1;
          if (b.role === 'pending' && a.role !== 'pending') return 1;
          return a.displayName.localeCompare(b.displayName);
        }),
      );
    } catch {
      setError('No se pudo cargar la lista de usuarios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const filtered = useMemo(() => {
    if (tab === 'pending') {
      return users.filter((u) => u.role === 'pending' || !u.active);
    }
    if (tab === 'active') {
      return users.filter((u) => u.active && u.role !== 'pending');
    }
    return users;
  }, [users, tab]);

  const pendingCount = users.filter(
    (u) => u.role === 'pending' || !u.active,
  ).length;

  function startEdit(user: UserProfile) {
    if (isAdminOnly && user.role === 'superadmin') {
      setError('Un administrador no puede modificar cuentas superadmin.');
      return;
    }
    setEditingId(user.uid);
    setForm({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      companyId: user.companyId,
      role: user.role === 'pending' ? 'commercial' : user.role,
      active: user.active,
      provider: user.provider,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.uid.trim()) {
      setError('El UID es obligatorio para altas manuales (email/password).');
      return;
    }
    if (form.role === 'pending') {
      setError('Asigná un rol distinto de pendiente al aprobar.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveUserProfile({
        ...form,
        uid: form.uid.trim(),
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        active: form.active,
      });
      resetForm();
      await loadUsers();
    } catch {
      setError('No se pudo guardar. Verificá reglas de Firestore y rol superadmin.');
    } finally {
      setSaving(false);
    }
  }

  async function quickApprove(user: UserProfile, role: UserRole = 'commercial') {
    setSaving(true);
    setError(null);
    try {
      await saveUserProfile({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        companyId: user.companyId,
        role,
        active: true,
        provider: user.provider,
      });
      await loadUsers();
    } catch {
      setError('No se pudo aprobar el usuario.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="users-page">
      <p>
        <Link to={modulePaths.admin}>← Administración</Link>
      </p>
      <h1>Usuarios y accesos</h1>
      <p className="auth-muted">
        Los ingresos con Google crean un perfil <strong>pendiente</strong>{' '}
        automáticamente. Aprobálos asignando rol y marcando como activo.
      </p>

      <div className="users-tabs">
        <button
          type="button"
          className={tab === 'pending' ? 'users-tab is-active' : 'users-tab'}
          onClick={() => setTab('pending')}
        >
          Pendientes ({pendingCount})
        </button>
        <button
          type="button"
          className={tab === 'active' ? 'users-tab is-active' : 'users-tab'}
          onClick={() => setTab('active')}
        >
          Activos
        </button>
        <button
          type="button"
          className={tab === 'all' ? 'users-tab is-active' : 'users-tab'}
          onClick={() => setTab('all')}
        >
          Todos
        </button>
      </div>

      <form className="users-form" onSubmit={handleSubmit}>
        <h2>
          {editingId
            ? `Editar — ${users.find((u) => u.uid === editingId)?.displayName ?? ''}`
            : 'Alta manual (email/password)'}
        </h2>
        <p className="auth-muted">
          Para Google no hace falta alta manual: el usuario aparece en pendientes
          al primer login.
        </p>
        <div className="users-form-grid">
          <label className="auth-label">
            UID (solo alta manual)
            <input
              value={form.uid}
              onChange={(e) => setForm({ ...form, uid: e.target.value })}
              placeholder="Firebase Auth UID"
              disabled={Boolean(editingId)}
            />
          </label>
          <label className="auth-label">
            Email
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Nombre
            <input
              required
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Rol
            <select
              value={form.role}
              onChange={(e) =>
                setForm({ ...form, role: e.target.value as UserRole })
              }
            >
              {ASSIGNABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {USER_ROLE_LABELS[role]}
                </option>
              ))}
              <option value="superadmin">{USER_ROLE_LABELS.superadmin}</option>
            </select>
          </label>
          <label className="auth-label auth-label--row">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Activo (puede entrar al ERP)
          </label>
        </div>
        {error ? <p className="auth-error">{error}</p> : null}
        <div className="users-form-actions">
          <button type="submit" className="auth-button" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          {editingId ? (
            <button
              type="button"
              className="auth-button auth-button--secondary"
              onClick={resetForm}
            >
              Cancelar
            </button>
          ) : null}
        </div>
      </form>

      {editingId ? (
        <EntityAuditTrail entityType="user" entityId={editingId} />
      ) : null}

      <h2>Listado</h2>
      {loading ? <p className="auth-muted">Cargando…</p> : null}
      <table className="users-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Proveedor</th>
            <th>Rol</th>
            <th>Activo</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((user) => (
            <tr key={user.uid}>
              <td>
                <div className="users-cell-user">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt=""
                      className="users-avatar"
                      width={32}
                      height={32}
                    />
                  ) : null}
                  <span>
                    <strong>{user.displayName}</strong>
                    <br />
                    <span className="auth-muted">{user.email}</span>
                  </span>
                </div>
              </td>
              <td>{user.provider}</td>
              <td>{USER_ROLE_LABELS[user.role]}</td>
              <td>{user.active ? 'Sí' : 'No'}</td>
              <td className="users-actions-cell">
                {user.role === 'pending' || !user.active ? (
                  <button
                    type="button"
                    className="link-button"
                    disabled={saving}
                    onClick={() => void quickApprove(user, 'commercial')}
                  >
                    Aprobar (comercial)
                  </button>
                ) : null}
                {isAdminOnly && user.role === 'superadmin' ? (
                  <span className="auth-muted">—</span>
                ) : (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => startEdit(user)}
                  >
                    Editar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && filtered.length === 0 ? (
        <p className="auth-muted">No hay usuarios en este filtro.</p>
      ) : null}
    </section>
  );
}
